/**
 * The outbox: queue locally, sync later, never lose a dose.
 *
 * The scenario is literal. Richard is in the basement with no signal and takes his
 * propranolol at 07:12. That checkmark and that time must survive.
 */
import type { LogEventInput, SyncResult } from '@rx/shared';
import type { AppConfig } from '../config.js';
import { ApiError, OfflineError, apiFetch, clockSkewMs } from '../api/client.js';
import { dequeue, enqueue, markAttempt, outboxEntries } from './db.js';
import { uuidv7 } from './ids.js';

/**
 * Fields the caller does not supply — they are properties of the moment, not the action.
 *
 * Distributive: a plain Omit over a discriminated union collapses it into one shapeless
 * object and loses the per-variant fields entirely.
 */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

type EventDraft = DistributiveOmit<
  LogEventInput,
  'eventId' | 'clientCapturedAt' | 'clientTz' | 'clientTzOffsetMinutes' | 'clockSkewMsAtCapture'
>;

export interface QueuedEvent {
  eventId: string;
  event: LogEventInput;
}

/**
 * Stamp an action with the moment it happened and queue it.
 *
 * The time is captured HERE, at the tap — never at sync. A dose taken at 07:12 and
 * synced at 12:40 is a 07:12 dose, and the server is built to honour that.
 */
export async function queueEvent(patientId: string, draft: EventDraft): Promise<QueuedEvent> {
  const now = new Date();
  const event = {
    ...draft,
    eventId: uuidv7(now.getTime()),
    clientCapturedAt: now.toISOString(),
    clientTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    clientTzOffsetMinutes: -now.getTimezoneOffset(),
    clockSkewMsAtCapture: await clockSkewMs(),
    capturedOffline: !navigator.onLine,
  } as LogEventInput;

  await enqueue({
    eventId: event.eventId,
    patientId,
    event,
    queuedAt: now.toISOString(),
    attempts: 0,
  });

  return { eventId: event.eventId, event };
}

export interface DrainOutcome {
  attempted: number;
  applied: number;
  rejected: number;
  offline: boolean;
}

let draining = false;

/**
 * Push the backlog.
 *
 * Entries are removed ONLY for ids the server acknowledged. A crash between the
 * response and the local delete replays those ids next time, and the server's
 * structural idempotency turns the replay into a no-op.
 */
export async function drainOutbox(config: AppConfig): Promise<DrainOutcome> {
  if (draining) return { attempted: 0, applied: 0, rejected: 0, offline: false };
  draining = true;
  try {
    const entries = await outboxEntries();
    if (entries.length === 0) return { attempted: 0, applied: 0, rejected: 0, offline: false };

    let applied = 0;
    let rejected = 0;

    // Batched per patient; the API caps a batch at 50.
    for (const patientId of new Set(entries.map((e) => e.patientId))) {
      const forPatient = entries.filter((e) => e.patientId === patientId).slice(0, 50);
      try {
        const result = await apiFetch<SyncResult>(config, '/v1/sync', {
          method: 'POST',
          body: { patientId, events: forPatient.map((e) => e.event) },
        });
        await dequeue(result.applied.map((a) => a.eventId));
        applied += result.applied.length;
        rejected += result.rejected.length;

        for (const failure of result.rejected) {
          await markAttempt(failure.eventId, failure.reason);
        }
      } catch (error) {
        if (error instanceof OfflineError) {
          return { attempted: entries.length, applied, rejected, offline: true };
        }
        // 401 means the token could not be refreshed — still offline, or signed out.
        // Either way the entries stay queued; a log is never discarded.
        if (error instanceof ApiError && error.status === 401) {
          return { attempted: entries.length, applied, rejected, offline: true };
        }
        for (const entry of forPatient) {
          await markAttempt(entry.eventId, error instanceof Error ? error.message : 'unknown');
        }
      }
    }

    return { attempted: entries.length, applied, rejected, offline: false };
  } finally {
    draining = false;
  }
}

/**
 * Every trigger that should push the backlog.
 *
 * Background Sync alone is not enough: iOS Safari does not implement it, so without
 * the foreground triggers an iPhone would queue doses and silently never send them.
 */
export function registerDrainTriggers(config: AppConfig, onDrain: () => void): () => void {
  const run = (): void => {
    void drainOutbox(config).then(onDrain);
  };

  const onVisible = (): void => {
    if (document.visibilityState === 'visible') run();
  };

  window.addEventListener('online', run);
  document.addEventListener('visibilitychange', onVisible);
  const interval = window.setInterval(run, 60_000);
  run();

  // Where Background Sync exists, use it as well — it can push while the tab is closed.
  // serviceWorker is typed as always present but is absent in insecure contexts and
  // some privacy modes.
  const sw = (navigator as unknown as { serviceWorker?: ServiceWorkerContainer }).serviceWorker;
  void sw?.ready
    .then((registration) => {
      const sync = (registration as ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } })
        .sync;
      return sync?.register('outbox');
    })
    .catch(() => undefined);

  return () => {
    window.removeEventListener('online', run);
    document.removeEventListener('visibilitychange', onVisible);
    window.clearInterval(interval);
  };
}
